import { useToast } from '@chakra-ui/react';
import { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { CreateOrganizationSportGameDurationDto } from '../../../models/organization/dto/organization-sport-game-duration.dto';
import { useCreateOrganizationGameDuration } from '../../organization/hooks/useOrganizationGameDurations';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { buildDurationWriteWarning } from '../utils/duration-write-warnings';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const SPORT_OPTIONS = [
  { value: SportType.PADEL, label: 'Padel' },
  { value: SportType.TENNIS, label: 'Tennis' },
  { value: SportType.BASKETBALL, label: 'Basketball' },
  { value: SportType.VOLLEYBALL, label: 'Volleyball' },
  { value: SportType.BADMINTON, label: 'Badminton' },
  { value: SportType.FOOTBALL, label: 'Football' },
];

export default function CreateGameDurationForm({ isOpen, onClose }: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  const [sport, setSport] = useState<SportType>(SportType.PADEL);
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const toast = useToast();

  const { mutateAsync: createGameDuration, isLoading } = useCreateOrganizationGameDuration(
    selectedOrganization?.id || '',
    (result) => {
      // Offering a duration nothing prices is the additive edit that takes the
      // whole sport offline: there is no catch-all price on the duration axis,
      // so the booking grid comes back empty for any player whose picker lands
      // on it. The write is legitimate and succeeded — pricing after
      // configuring is a normal order of operations — so this is a warning
      // naming the missing step, not an error.
      const warning = buildDurationWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_durations.duration_created'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        handleClose();
        return;
      }
      toast({
        title: translate('game_durations.duration_created'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      handleClose();
    }
  );

  const handleClose = () => {
    // Reset form when closing
    setSport(SportType.PADEL);
    setDurationMinutes(60);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrganization?.id) return;

    const formData: CreateOrganizationSportGameDurationDto = {
      sport,
      durationMinutes,
    };

    try {
      await createGameDuration(formData);
    } catch (error: any) {
      toast({
        title: translate('game_durations.error'),
        description: error?.message || translate('game_durations.error_creating'),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('game_durations.create_duration')}
      onSubmit={handleSubmit}
      submitLabel={translate('game_durations.save')}
      cancelLabel={translate('game_durations.cancel')}
      isSubmitting={isLoading}
      isSubmitDisabled={!selectedOrganization?.id}
      size="md"
    >
      <FormStack>
        <FormField
          type="select"
          label={translate('game_durations.sport')}
          value={sport}
          onChange={(value) => setSport(value as SportType)}
          options={SPORT_OPTIONS}
        />
        <FormField
          type="number"
          label={translate('game_durations.duration_minutes')}
          value={durationMinutes}
          onChange={(value) => setDurationMinutes(value || 60)}
          min={1}
          max={480}
        />
      </FormStack>
    </FormModal>
  );
}